import React, {Component} from 'react';
import _ from 'lodash';
import {safeMult, createUUID, getExtension} from 'common/utils';
import {API_ATTACHMENT_UPLOAD_SUCCESS, API_ATTACHMENT_DELETE_SUCCESS} from 'common/types';
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_SIZE,
  ATTACHMENT_TYPES,
} from 'app-ui-componentlibrary/dist/common/constants';
import UploadPresentational from './UploadPresentational';
import UploadReviewPresentational from './UploadReviewPresentational';
import UploadReviewPresentational_V2 from './v2/UploadPresentational';
import produce from 'immer';
import {connect} from 'react-redux';

const presentationalNameMap = {
  panel: UploadReviewPresentational,
  basic: UploadPresentational,
};

export const sizeError = (size, is1GbAttachmentAccepted) => {
  if (!is1GbAttachmentAccepted) {
    return `Your file is too large. Files must be less than ${size}MB.`;
  } else {
    return `The file size must be less than or equal to 1 GB.`;
  }
};

export const sizeZeroError = () => {
  return `Your file is too small. Files must be greater than 0mb.`;
};

export const extensionError = (acceptedFileTypes) =>
  `Your file extension is invalid. Files must be one of: ${acceptedFileTypes.join(
    ', '
  )}.`;

export const tooManyFilesError = (limit) =>
  `You are attempting to exceed the maximum number of files allowed. You cannot upload more than ${limit} file(s).`;

export const duplicateError = () =>
  `A document with the same filename and file type already exists.`;

export class UploaderController extends Component {
  constructor(props) {
    super(props);
    const {savedAttachments, fileSizeLimit, acceptedFileTypes} = this.props;
    this.state = {
      filesSuccessful: _.cloneDeep(savedAttachments) || [],
      filesFailed: [],
      isSupportedFile: null,
      isFileSizeLimit: null,
      saving: false,
      errorMessages: '',
      attachmentError: [],
      acceptedFileTypes: acceptedFileTypes || [],
      fileSizeLimit: fileSizeLimit || null,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(this.props.savedAttachments, prevProps.savedAttachments)) {
      this.setState({
        filesSuccessful: _.cloneDeep(this.props.savedAttachments) || [],
      });
    }
    if (
      !_.isEqual(prevState.filesSuccessful, this.state.filesSuccessful) &&
      this.props.currentlyCollaborating
    ) {
      this.validate();
    }
  }

  /** DEFAULT PROPS that match current backend requirements **/
  static defaultProps = {
    acceptedFileTypes: ACCEPTED_FILE_TYPES,
    fileSizeLimit: ACCEPTED_FILE_SIZE,
    chooseMulti: false,
  };

  processFile(file, error) {
    let result = {
      fileName: file.fileName,
      submissionId: file.submissionId,
    };
    if (error) {
      result.error = error;
    }
    return result;
  }

  handleUpload = (files) => {
    const {
      attachmentType,
      acceptedFileTypes,
      fileSizeLimit,
      numAllowed,
      is1GbAttachmentAccepted,
      avoid1GbAttachmentSaveError,
      avoid0MbAttachmentSaveError,
      userId,
    } = this.props;
    let curr = {
      // if multiple files, add on, otherwise we want to clear them out
      filesSuccessful: _.cloneDeep(this.state.filesSuccessful),
      // on each file upload attempt, reset failed files in UI
      filesFailed: [],
    };
    let toUpload = [];

    // reject all new files, user is adding too many
    if (numAllowed && this.state.filesSuccessful.length + files.length > numAllowed) {
      _.forEach(files, (file) => {
        let fileAttrs = {
          fileName: file.name,
          file,
        };
        curr.filesFailed.push(this.processFile(fileAttrs, tooManyFilesError(numAllowed)));
      });
    } else {
      let errorList = this.state.attachmentError;
      _.forEach(files, (file) => {
        let fileName = file.name;
        let attachmentExtension = '.' + getExtension(fileName);
        let fileSizeLmtInBytes = safeMult(fileSizeLimit, 1048576); // Converting to Bytes

        let fileAttrs = {
          submissionId: createUUID(),
          file,
          fileName,
        };
        if (!acceptedFileTypes.includes(attachmentExtension.toLowerCase())) {
          curr.filesFailed.push(
            this.processFile(fileAttrs, extensionError(acceptedFileTypes))
          );
          errorList.push(extensionError(acceptedFileTypes));
          if (avoid1GbAttachmentSaveError === null) {
            this.props.saveDataErrors('attachmentError', errorList);
          }
        } else if (file.size > fileSizeLmtInBytes) {
          curr.filesFailed.push(
            this.processFile(fileAttrs, sizeError(fileSizeLimit, is1GbAttachmentAccepted))
          );
          errorList.push(sizeError(fileSizeLimit, is1GbAttachmentAccepted));
          if (avoid1GbAttachmentSaveError === null) {
            console.log('avoid1GbAttachmentSaveError: ', avoid1GbAttachmentSaveError);
            this.props.saveDataErrors('attachmentError', errorList);
          }
        } else if (file.size == 0) {
          curr.filesFailed.push(this.processFile(fileAttrs, sizeZeroError()));
          errorList.push(sizeZeroError());
          if (avoid0MbAttachmentSaveError === null) {
            this.props.saveDataErrors('attachmentError', errorList);
          }
        } else {
          toUpload.push(fileAttrs);
        }
        _.forEach(curr.filesSuccessful, (file) => {
          if (file.fileName === fileAttrs.fileName) {
            curr.filesFailed.push(this.processFile(fileAttrs, duplicateError()));
          }
        });
      });
      this.setState({
        attachmentError: errorList,
      });
    }

    let processedToUpload = _.map(toUpload, (file) => this.processFile(file));

    if (curr.filesFailed.length === 0) {
      this.setState({
        saving: true,
      });
      this.props
        .uploadAttachment(attachmentType, toUpload)
        .then((res) => {
          if (res && res.type === API_ATTACHMENT_UPLOAD_SUCCESS) {
            let newlySuccesful = _.map(res.payload, (file) => {
              const currDate = new Date().toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
              });
              return {
                fileName: file.fileName,
                resourceId: file.resourceId, // used for downloading file
                deletable: file.deletable,
                submissionId: file.submissionId,
                status: file.status,
                uploadedDate: currDate,
                uploadedBy: userId,
                fileSize: file.fileSize,
              };
            });
            let filesNotSubmitted = _.differenceWith(
              processedToUpload,
              newlySuccesful,
              (file1, file2) => {
                return file1.submissionId === file2.submissionId;
              }
            );

            curr.filesSuccessful = curr.filesSuccessful.concat(newlySuccesful);
            curr.filesFailed = curr.filesFailed.concat(filesNotSubmitted);
          } else {
            // all files have failed
            curr.filesSuccessful = this.state.filesSuccessful;
            curr.filesFailed = curr.filesFailed.concat(processedToUpload);
          }
          return curr;
        })
        .catch((err) => {
          curr.filesFailed = curr.filesFailed.concat(processedToUpload);
          return curr;
        })
        .then((filesToAdd) => {
          this.setState({
            ...this.state,
            saving: false,
            filesFailed: filesToAdd.filesFailed,
          });
          return this.resync(curr.filesSuccessful);
        })
        .then((synced) => {
          if (synced) {
            this.validate();
          }
        });
    } else {
      let allNewFiles = curr.filesFailed.concat(processedToUpload);
      this.setState({
        ...this.state,
        filesFailed: allNewFiles,
      });
    }
  };

  handleDelete = (removedFileId) => {
    this.props
      .deleteAttachment(removedFileId)
      .then((res) => {
        if (res && res.type === API_ATTACHMENT_DELETE_SUCCESS) {
          return this.resync(
            _.filter(
              this.state.filesSuccessful,
              (file) => !_.isEqual(file.resourceId, removedFileId)
            )
          );
        }
      })
      .then((synced) => {
        if (synced) {
          this.validate();
        }
      });
  };

  handleUpdateDescription = (payload) => {
    const {sessionDataPath} = this.props;

    this.props.updateDescription(payload).then((res) => {
      if (res && res.type === API_ATTACHMENT_UPLOAD_SUCCESS && sessionDataPath) {
        return this.props.updateRedux(this.state.filesSuccessful, true);
      }
    });
  };

  validate = () => {
    const {numRequired, numAllowed} = this.props;
    let required = numRequired || -1;
    let allowed = numAllowed || Infinity;
    // user is blocked from uploading too many, but double checking
    const isValid =
      this.state.filesSuccessful.length >= required &&
      this.state.filesSuccessful.length <= allowed;
    let error = '';
    let errorList = this.state.attachmentError;
    if (!isValid) {
      let standardError = `Invalid number of attachments: You must add at least ${numRequired} attachment.`;
      error = this.props.errorMessage ? this.props.errorMessage : standardError;
      let newErrList = produce(errorList, (draft) => {
        draft.push(error);
      });
      this.props.saveDataErrors('attachmentError', newErrList);
    }
    this.setState({
      errorMessages: error,
      attachmentError: errorList,
    });
  };

  resync = (filesSuccessful) => {
    this.setState({
      filesFailed: [],
      filesSuccessful,
    });
    this.props.resync();
    const nonDeletableAttachments = _.get(this.props, 'nonDeletableAttachments', []);
    const filesToSync = [...nonDeletableAttachments, ...filesSuccessful];
    return this.props.updateRedux(filesToSync);
  };

  render() {
    const {filesSuccessful, filesFailed, saving} = this.state;
    let {
      attachmentType,
      acceptedFileTypes,
      chooseMulti,
      fileSizeLimit,
      displayFormat,
      guidance,
      header,
      extended_attachment,
      showAcceptedFileTypes,
      hideUploadedBy,
      hrefId,
      buttonMsg,
      hideDeleteforNumRequired,
      numRequired,
      hideAction,
      currentlyCollaborating,
      sortBy,
      sortOrder,
    } = this.props;

    let {errorMessages} = this.state;

    let Presentational = '';
    if (extended_attachment) {
      Presentational = UploadReviewPresentational_V2;
    } else {
      Presentational = presentationalNameMap[displayFormat] || UploadPresentational;
    }

    const propsToPass = {
      buttonMsg: buttonMsg,
      handleChange: this.handleUpload,
      handleDelete: this.handleDelete,
      updateDescription: this.handleUpdateDescription,
      disabled: saving,
      guidance: guidance,
      validate: this.validate,
      filesSuccessful,
      filesFailed,
      attachmentType,
      chooseMulti,
      acceptedFileTypes,
      fileSizeLimit,
      errorMessages,
      header,
      extended_attachment,
      hideUploadedBy,
      showUnsubmittedOnly: this.props.showUnsubmittedOnly,
      showAcceptedFileTypes,
      hrefId,
      numRequired,
      hideDeleteforNumRequired: hideDeleteforNumRequired,
      hideAction: hideAction,
      currentlyCollaborating,
      sortBy,
      sortOrder,
    };

    return <Presentational {...propsToPass} />;
  }
}

export const mapStateToProps = (state) => {
  return {
    userId: state.auth.userId ? state.auth.userId : state.profile.userId,
  };
};
export default connect(mapStateToProps)(UploaderController);
